import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Map as MapIcon, Star, Ticket, Plus } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { CityMap } from "@/components/CityMap";
import { AREAS, getArea, canonicalCoords } from "@/data/areas";
import { AREA_FAQ, AREA_LINKS } from "@/data/areaFaq";
import { SPOTS } from "@/data/planner";
import { useActiveAds, adsForArea } from "@/lib/ads";
import { GygActivities, GygTourBand } from "@/components/GetYourGuide";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";

// Per-area GetYourGuide config. Add areas here as links come in.
//  • { type: "availability", tourId: "616173", variant: "horizontal" }  → single-tour booking widget
//  • { type: "activities", tourIds: "111,222", numberOfItems: 3 }        → list of tours
const AREA_TOURS = {
  "indre-by": { type: "availability", tourId: "37848", variant: "horizontal" },
  "islands-brygge": { type: "availability", tourId: "78025", variant: "horizontal" },
  christianshavn: { type: "availability", tourId: "616173", variant: "horizontal" }, // Christiania & Christianshavn walking tour
  refshaleoen: { type: "availability", tourId: "1018298", variant: "horizontal" },
  frederiksberg: { type: "availability", tourId: "441421", variant: "horizontal" },
  vesterbro: { type: "availability", tourId: "72048", variant: "horizontal" },
  norrebro: { type: "availability", tourId: "504350", variant: "horizontal" }, // Nørrebro guided tour
  osterbro: { type: "availability", tourId: "78025", variant: "horizontal" },
  nordhavn: { type: "availability", tourId: "125759", variant: "horizontal" },
  nordvest: { type: "availability", tourId: "686879", variant: "horizontal" },
  "carlsberg-byen": { type: "availability", tourId: "727418", variant: "horizontal" },
  amager: { type: "availability", tourId: "1200862", variant: "horizontal" }, // Hidden Gems social boat tour (harbour island fit)
};

// Which planner cluster(s) each area covers, so every planner activity is
// surfaced as a pin on its area page. Indre By absorbed the old "harbour"
// cluster (Nyhavn, Amalienborg, Little Mermaid, canal tour, harbour swims…).
const AREA_CLUSTERS = {
  "indre-by": ["indre-by", "harbour"],
  christianshavn: ["christianshavn"],
  "islands-brygge": ["islands-brygge"],
  refshaleoen: ["refshaleoen"],
  vesterbro: ["vesterbro"],
  norrebro: ["norrebro"],
  osterbro: ["osterbro"],
  amager: ["amager"],
  frederiksberg: ["frederiksberg"],
};

export default function AreaDetail() {
  const { id } = useParams();
  const area = getArea(id);
  const allAds = useActiveAds();
  if (!area) return <Navigate to="/areas" replace />;

  const areaAds = adsForArea(allAds, area.id);
  const areaTours = AREA_TOURS[area.id];

  // Per-area FAQ (visible accordion + matching FAQPage JSON-LD).
  const faq = AREA_FAQ[area.name];
  const faqQ = faq?.questions || {};
  const faqItems = faq
    ? [
        ...(faq.where ? [{ q: faqQ.where || `Where is ${area.name}?`, a: faq.where }] : []),
        ...(faq.worth ? [{ q: faqQ.worth || `Is ${area.name} worth visiting?`, a: faq.worth }] : []),
        { q: faqQ.do || `What are the best things to do in ${area.name}?`, a: faq.do || `The highlights: ${(area.highlights || []).join(", ")}.`, ...(faq.doLink ? { link: faq.doLink } : {}) },
        { q: faqQ.streets || `What are the best streets in ${area.name}?`, a: faq.streets },
        { q: faqQ.bakeries || `Where are the best bakeries in ${area.name}?`, a: faq.bakeries, link: { to: "/bakeries", label: "The bakery guide" } },
        { q: faqQ.eat || `What should I eat in ${area.name}?`, a: faq.eat, ...(faq.eatLink ? { link: faq.eatLink } : {}) },
        { q: faqQ.coffee || `Where's the best coffee in ${area.name}?`, a: faq.coffee },
        ...(faq.drink ? [{ q: faqQ.drink || `Where to go for a drink in ${area.name}?`, a: faq.drink }] : []),
        { q: faqQ.gettingThere || `How do I get to ${area.name}?`, a: faq.gettingThere },
        { q: faqQ.howLong || `How long should I spend in ${area.name}?`, a: faq.howLong },
        { q: faqQ.safe || `Is ${area.name} safe?`, a: faq.safe },
        { q: faqQ.traveller || `Who is ${area.name} best for?`, a: faq.traveller },
      ]
    : [];
  const FAQ_SCHEMA = faqItems.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  const idx = AREAS.findIndex((a) => a.id === id);
  const prev = idx > 0 ? AREAS[idx - 1] : null;
  const next = idx >= 0 && idx < AREAS.length - 1 ? AREAS[idx + 1] : null;

  // Every planner activity for this area, surfaced as pins beneath the top
  // picks — merged with the curated highlights and de-duplicated by name.
  // Temporary events (Christmas markets, festivals, Pride), generic
  // strolls/meals (hidePin) and planner-only stops (noAreaPin, e.g. the Opera
  // House) are left out — pins are permanent, curated places.
  const clusters = AREA_CLUSTERS[area.id] || [];
  const clusterSpots = SPOTS.filter(
    (s) => clusters.includes(s.cluster) && !s.event && !s.hidePin && !s.noAreaPin
  );
  const plannerActivities = clusterSpots.map((s) => s.name);
  const areaPins = Array.from(new Set([...(area.highlights || []), ...plannerActivities]));

  // Build the small map so it matches the "things to see & do" pins: plot every
  // pin that has a known location, then fill in any concrete pick / map-extra
  // place not already shown. Coordinates come from the planner SPOTS (the
  // corrected source of truth) first, then picks, then map extras; entries are
  // de-duplicated by both name and location so the same spot never doubles up.
  const normName = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  const coordByName = {};
  const addCoord = (name, coords) => {
    if (!coords) return;
    const k = normName(name);
    if (!(k in coordByName)) coordByName[k] = coords;
  };
  clusterSpots.forEach((s) => addCoord(s.name, s.coords));
  (area.picks || []).forEach((p) => addCoord(p.name, p.coords));
  (area.mapExtras || []).forEach((p) => addCoord(p.name, p.coords));

  const seenMarkers = new Set();
  const mapMarkers = [];
  const pushMarker = (name, rawCoords) => {
    if (!rawCoords) return;
    const coords = canonicalCoords(name, rawCoords);
    const nk = normName(name);
    const ck = `${coords[0].toFixed(3)},${coords[1].toFixed(3)}`;
    if (seenMarkers.has(nk) || seenMarkers.has(ck)) return;
    seenMarkers.add(nk);
    seenMarkers.add(ck);
    mapMarkers.push({ name, coords, color: area.color, label: name });
  };
  areaPins.forEach((name) => pushMarker(name, coordByName[normName(name)]));
  (area.picks || []).forEach((p) => pushMarker(p.name, p.coords));
  (area.mapExtras || []).forEach((p) => pushMarker(p.name, p.coords));

  // Sponsored partner pins for this area (gold), shown on the map but kept out
  // of the curated walking route above.
  areaAds.forEach((ad) => {
    if (Array.isArray(ad.coords)) {
      mapMarkers.push({
        name: ad.business_name,
        coords: ad.coords,
        color: "#F59E0B",
        label: "Ad",
        tag: `Sponsored · ${ad.category}`,
      });
    }
  });

  // Tourists Google "things to do in X" — and for Indre By they search
  // "Copenhagen city centre", not the Danish name, so it gets its own title.
  const seoTitle =
    area.id === "indre-by"
      ? "Copenhagen City Centre Guide: Things to Do in Indre By"
      : `Things to Do in ${area.name} — Copenhagen Area Guide`;

  // TouristAttraction structured data so each neighbourhood is eligible for
  // rich results and place-based search (geo + address + parent destination).
  const ATTRACTION_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    name: `${area.name}, Copenhagen`,
    description: area.blurb,
    url: `https://cphhighlights.com/areas/${area.id}`,
    image: area.image,
    ...(Array.isArray(area.coords)
      ? { geo: { "@type": "GeoCoordinates", latitude: area.coords[0], longitude: area.coords[1] } }
      : {}),
    address: { "@type": "PostalAddress", addressLocality: "Copenhagen", addressCountry: "DK" },
    containedInPlace: { "@type": "TouristDestination", name: "Copenhagen, Denmark" },
    ...(areaPins.length ? { touristType: "Sightseeing", keywords: areaPins.join(", ") } : {}),
  };

  return (
    <main data-testid="area-detail-page" className="pt-16 md:pt-20">
      <Seo
        title={seoTitle}
        description={area.blurb}
        path={`/areas/${area.id}`}
        image={area.image}
      />
      <PageSchema
        name={seoTitle}
        description={area.blurb}
        path={`/areas/${area.id}`}
        crumb={area.name}
      />
      {FAQ_SCHEMA && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
        </Helmet>
      )}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ATTRACTION_SCHEMA)}</script>
      </Helmet>
      {/* hero */}
      <section className="relative border-b border-zinc-200">
        <div className="relative h-[46vh] min-h-[340px] overflow-hidden">
          <img src={area.image} alt={`${area.name}, a Copenhagen neighbourhood — ${area.tag}`} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/45" />
          <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 h-full flex flex-col justify-end pb-10">
            <div className="self-start flex items-center gap-3 mb-6">
              <Link
                to="/areas"
                data-testid="detail-back-areas"
                className="inline-flex items-center gap-2 font-body font-700 text-sm bg-white text-black px-5 py-2.5 rounded-full border border-zinc-200 hover:bg-[#F9F8F6] transition-colors"
              >
                <ArrowLeft size={16} /> All areas
              </Link>
              <ShareButton title={`${area.name} — Copenhagen Area Guide`} testid="area-detail-share" />
            </div>
            <span
              className="self-start font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white mb-4"
            >
              {area.tag.toUpperCase()}
            </span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl text-white"
            >
              {area.name}
            </motion.h1>
          </div>
        </div>
      </section>

      {/* body */}
      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* left: blurb + picks */}
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-body text-lg md:text-xl text-black/80 leading-relaxed max-w-2xl">
                {area.blurb}
              </p>
            </Reveal>

            <Reveal delay={0.05}>
              <h2 className="font-display font-900 tracking-tight text-2xl md:text-3xl mt-12 mb-6">Top picks</h2>
            </Reveal>

            <div className="border-t border-dotted border-zinc-300">
              {area.picks.map((p, i) => (
                <Reveal key={p.name} delay={i * 0.05}>
                  <div className="group grid grid-cols-12 gap-3 items-baseline border-b border-zinc-200 py-5 hover:bg-white transition-colors px-2">
                    <span className="col-span-2 font-editorial text-3xl md:text-4xl text-black/20 group-hover:text-black transition-colors">
                      0{i + 1}
                    </span>
                    <div className="col-span-10">
                      <h3 className="font-display font-800 tracking-tight text-xl md:text-2xl leading-none">
                        {p.link ? (
                          <a
                            href={p.link}
                            target="_blank"
                            rel="noopener noreferrer sponsored"
                            data-testid={`area-pick-link-${i}`}
                            className="hover: transition-colors underline decoration-transparent hover:decoration-[#DF3226]/50 underline-offset-4"
                          >
                            {p.name}
                          </a>
                        ) : (
                          p.name
                        )}
                      </h3>
                      <p className="font-body text-sm md:text-base text-black/70 mt-1.5">{p.note}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.05}>
              <div className="flex items-center gap-2 mt-10 mb-3">
                <MapPin size={18} style={{ color: area.color }} />
                <h3 className="font-body font-800 text-xs tracking-[0.25em]">THINGS TO SEE &amp; DO HERE</h3>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="area-activity-pins">
                {areaPins.map((h) => (
                  <span key={h} data-testid={`area-pin-${h}`} className="inline-flex items-center gap-1.5 font-body font-700 text-sm bg-white border border-zinc-200 rounded-full px-3 py-1.5">
                    <MapPin size={13} style={{ color: area.color }} />
                    {h}
                  </span>
                ))}
              </div>
            </Reveal>

            {areaAds.length > 0 && (
              <Reveal delay={0.05}>
                <div className="flex items-center gap-2 mt-12 mb-4" data-testid="area-partners">
                  <Star size={18} className="text-[#F59E0B] fill-[#F59E0B]" />
                  <h3 className="font-body font-800 text-xs tracking-[0.25em]">LOCAL PARTNERS</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {areaAds.map((ad) => (
                    <div
                      key={ad.id}
                      data-testid={`area-partner-${ad.id}`}
                      className="bg-white border border-zinc-200 shadow-brut p-5 relative"
                    >
                      <span className="absolute top-3 right-3 font-body font-800 text-[10px] tracking-widest text-[#F59E0B]">
                        SPONSORED
                      </span>
                      <p className="font-body font-700 text-[11px] tracking-widest text-black/40">{ad.category}</p>
                      {ad.website ? (
                        <a
                          href={ad.website}
                          target="_blank"
                          rel="noopener noreferrer nofollow sponsored"
                          data-testid={`area-partner-link-${ad.id}`}
                          className="font-display font-900 tracking-tight text-lg mt-1 inline-block text-black underline decoration-1 underline-offset-2 decoration-black/30 hover:decoration-[#DF3226] hover:text-[#DF3226] transition-colors"
                        >
                          {ad.business_name}
                        </a>
                      ) : (
                        <h4 className="font-display font-900 tracking-tight text-lg mt-1">{ad.business_name}</h4>
                      )}
                      <p className="font-body text-sm text-black/70 mt-1.5 leading-relaxed">{ad.description}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}
          </div>

          {/* right: mini map */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="lg:sticky lg:top-28">
                <div className="flex items-center gap-2 mb-3">
                  <MapIcon size={16} />
                  <span className="font-body font-800 text-xs tracking-[0.25em]">ON THE MAP</span>
                </div>
                <div className="border border-zinc-200 shadow-brut-lg overflow-hidden h-[380px] md:h-[460px]">
                  <CityMap
                    key={area.id}
                    center={area.coords}
                    zoom={14}
                    scrollWheelZoom={false}
                    fitToMarkers
                    markers={mapMarkers}
                  />
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Link
                    to="/map"
                    data-testid="detail-open-full-map"
                    className="inline-flex items-center gap-2 font-display font-800 bg-white px-5 py-3 rounded-full border border-zinc-200 shadow-brut-sm hover:-translate-y-0.5 hover:shadow-lg transition-all"
                  >
                    <MapIcon size={16} /> Full city map
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* contextual internal links */}
      {(AREA_LINKS[area.name] || []).length > 0 && (
        <section data-testid="area-explore-links" className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
          <div className="max-w-[1000px] mx-auto px-5 md:px-10 py-8 md:py-10 flex flex-col gap-3">
            {(AREA_LINKS[area.name] || []).map((l, i) => (
              <Reveal key={l.to || l.href} delay={i * 0.05}>
                {l.href ? (
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    data-testid={`area-explore-link-${i}`}
                    className="group inline-flex items-center gap-2 font-body font-700 text-base md:text-lg text-black/80 hover:text-[#DF3226] transition-colors"
                  >
                    <Ticket size={18} className="text-[#DF3226] shrink-0 group-hover:translate-x-1 transition-transform" />
                    {l.label}
                  </a>
                ) : (
                  <Link
                    to={l.to}
                    data-testid={`area-explore-link-${i}`}
                    className="group inline-flex items-center gap-2 font-body font-700 text-base md:text-lg text-black/80 hover:text-[#DF3226] transition-colors"
                  >
                    <ArrowRight size={18} className="text-[#DF3226] shrink-0 group-hover:translate-x-1 transition-transform" />
                    {l.label}
                  </Link>
                )}
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* prev / next */}
      <section className="bg-black text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 grid grid-cols-2 gap-4">
          {prev ? (
            <Link to={`/areas/${prev.id}`} data-testid="detail-prev" className="group flex flex-col items-start">
              <span className="font-body font-700 text-xs tracking-widest text-white/50 flex items-center gap-1">
                <ArrowLeft size={14} /> PREVIOUS
              </span>
              <span className="font-display font-900 text-2xl md:text-3xl group-hover:text-[#DF3226] transition-colors">
                {prev.name}
              </span>
            </Link>
          ) : <span />}
          {next && (
            <Link to={`/areas/${next.id}`} data-testid="detail-next" className="group flex flex-col items-end text-right col-start-2">
              <span className="font-body font-700 text-xs tracking-widest text-white/50 flex items-center gap-1">
                NEXT
              </span>
              <span className="font-display font-900 text-2xl md:text-3xl group-hover:text-[#DF3226] transition-colors">
                {next.name}
              </span>
            </Link>
          )}
        </div>
      </section>

      {/* Book a tour — GetYourGuide activities relevant to this area */}
      {areaTours &&
        (areaTours.type === "availability" ? (
          <GygTourBand key={area.id} tourId={areaTours.tourId} />
        ) : (
          <section data-testid="area-tours" className="py-12 md:py-16 bg-[#F9F8F6] border-t border-dotted border-zinc-300">
            <div className="max-w-[1400px] mx-auto px-5 md:px-10">
              <div className="flex items-center gap-2 mb-6">
                <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">BOOK A TOUR</p>
              </div>
              <GygActivities
                key={area.id}
                tourIds={areaTours.tourIds}
                numberOfItems={areaTours.numberOfItems || 3}
              />
            </div>
          </section>
        ))}

      {/* per-area FAQ — last content section */}
      {faqItems.length > 0 && (
        <section data-testid="area-faq" className="bg-white border-t border-dotted border-zinc-300">
          <div className="max-w-[1000px] mx-auto px-5 md:px-10 py-12 md:py-20">
            <Reveal>
              <p className="font-body font-700 text-xs uppercase tracking-[0.2em] text-[#DF3226] mb-4">
                Good to know
              </p>
              <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-4xl sm:text-5xl">
                {area.name}, <span className="">answered.</span>
              </h2>
            </Reveal>
            <div className="mt-8 divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
              {faqItems.map((item, i) => (
                <details key={item.q} data-testid={`area-faq-item-${i}`} className="group py-5">
                  <summary className="flex items-start justify-between gap-4 cursor-pointer list-none font-display font-800 text-lg text-black">
                    <span>{item.q}</span>
                    <Plus size={22} className="shrink-0 mt-1 text-[#DF3226] transition-transform duration-300 group-open:rotate-45" />
                  </summary>
                  <p className="font-body text-base text-black/70 mt-3 max-w-3xl leading-relaxed">
                    {item.a}
                  </p>
                  {item.link && (
                    <Link
                      to={item.link.to}
                      data-testid={`area-faq-link-${i}`}
                      className="group flex items-center gap-2 mt-3 font-body font-700 text-base text-black/60 hover:text-[#DF3226] transition-colors w-fit"
                    >
                      <ArrowRight size={16} className="text-[#DF3226] shrink-0 group-hover:translate-x-1 transition-transform" />
                      {item.link.label}
                    </Link>
                  )}
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <ShareBar title={`${area.name} — Copenhagen Area Guide`} />
    </main>
  );
}
